import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ProjectList } from "./ProjectList";
import { ProjectHeader } from "./project/ProjectHeader";
import { ProjectFormInputs } from "./project/ProjectFormInputs";
import { ProjectPreview } from "./project/ProjectPreview";
import { FeedFormatOptions } from "./project/FeedFormatOptions";
import { useNavigate } from "react-router-dom";

interface ProjectData {
  title: string;
  client: string;
  types: string[];
  image: File | null;
  link: string;
  featurePoints?: string[];
}

export const ProjectForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState<ProjectData>({
    title: "",
    client: "",
    types: [],
    image: null,
    link: "",
    featurePoints: [],
  });
  const [imagePreview, setImagePreview] = useState<string>("");
  const [embedCode, setEmbedCode] = useState<string>("");
  const [projects, setProjects] = useState<any[]>([]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProjectData({ ...projectData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTypeToggle = (type: string) => {
    setProjectData(prev => {
      const types = prev.types.includes(type.toLowerCase())
        ? prev.types.filter(t => t !== type.toLowerCase())
        : [...prev.types, type.toLowerCase()];
      return { ...prev, types };
    });
  };

  const generateEmbedCode = (data: ProjectData) => {
    return `<iframe src="https://bytehype.com/projects/${encodeURIComponent(data.title)}" width="100%" height="600" frameborder="0"></iframe>`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = generateEmbedCode(projectData);
    setEmbedCode(code);
    
    const newProject = {
      id: Date.now(),
      ...projectData,
      image: imagePreview,
      embedCode: code
    };
    
    setProjects(prev => [...prev, newProject]);
    
    setProjectData({
      title: "",
      client: "",
      types: [],
      image: null,
      link: "",
      featurePoints: [],
    });
    setImagePreview("");
    
    toast({
      title: "Project Created",
      description: "Your project has been created and the embed code is ready.",
    });
  };

  const handleDelete = (id: number) => {
    setProjects(prev => prev.filter(project => project.id !== id));
    toast({
      title: "Project Deleted",
      description: "The project has been removed successfully.",
    });
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode);
    toast({
      title: "Copied!",
      description: "Embed code copied to clipboard",
    });
  };

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 animate-fadeIn">
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
        <ProjectHeader />
        
        <div className="grid gap-6 sm:gap-8 md:grid-cols-2">
          <Card className="p-4 sm:p-6 shadow-xl bg-black/50 backdrop-blur-sm border-gray-800">
            <form onSubmit={handleSubmit} className="space-y-6">
              <ProjectFormInputs
                projectData={projectData}
                setProjectData={setProjectData}
                handleImageChange={handleImageChange}
                handleTypeToggle={handleTypeToggle}
              />
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-[#FEF7CD] to-[#F97316] text-black font-bold hover:opacity-90"
              >
                GENERATE EMBED CODE
              </Button>
            </form>
          </Card>

          <ProjectPreview
            imagePreview={imagePreview}
            embedCode={embedCode}
            onCopyCode={handleCopyCode}
          />
        </div>

        <FeedFormatOptions />

        {projects.length > 0 && (
          <ProjectList 
            projects={projects} 
            onDelete={handleDelete}
          />
        )}

        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 sm:mt-12">
          <Button
            onClick={() => navigate("/")}
            className="w-full sm:w-auto bg-gradient-to-r from-[#0EA5E9] to-[#22C55E] text-black font-semibold px-4 sm:px-6 py-2 hover:opacity-90 transition-opacity"
          >
            BACK TO CONTENT DASH
          </Button>
          <Button
            onClick={() => window.location.href = "https://bytehype.lovable.app"}
            className="w-full sm:w-auto bg-gradient-to-r from-[#22C55E] to-[#0EA5E9] text-black font-semibold px-4 sm:px-6 py-2 hover:opacity-90 transition-opacity"
          >
            BACK TO BYTEHYPE
          </Button>
        </div>
      </div>
    </div>
  );
};